<template>
  <v-card flat class="card-grid">
    <div class="doughnut-error">
      <Error v-if="errors.length" :errors="errors" />
    </div>
    <slot v-if="!loading" name="title"></slot>
    <v-skeleton-loader class="mb-5" v-else type="text" width="100px" />
    <div class="chart">
      <doughnut
        :datasets="
          !loading && measuresAreReady && datasets.length ? datasets : fakeData
        "
      >
        <div class="chart-box-text">
          <div>
            <span class="chart-text accentLight--text">{{
              !loading ? percentage : "-"
            }}</span>
            <span class="chart-text">%</span>
          </div>
          <div>
            <span class="a-description a-thin">{{
              !loading ? divisionPowerByExpected : "-%"
            }}</span>
          </div>
        </div>
      </doughnut>
    </div>
    <div v-if="!loading" class="">
      <div
        class="a-description a-thin mt-3 bottom-text"
        v-if="measuresAreReady"
      >
        <div>
          <span>{{ power.description }}</span>
          <span class="accentLight--text"
            >{{ power.getValue() }} {{ power.eu }}</span
          >
        </div>
        <v-divider vertical></v-divider>
        <div>
          <span>{{ nominal.description }}</span>
          <span class="dark--text"
            >{{ nominal.getValue() }} {{ nominal.eu }}</span
          >
        </div>
        <v-divider v-if="options.hasExpected" vertical></v-divider>
        <div v-if="options.hasExpected">
          <span>{{ expected.description }}</span>
          <span class="orange--text"
            >{{ expected.getValue() }} {{ expected.eu }}</span
          >
        </div>
      </div>
      <div class="novalues" v-else>
        <div class="d-flex flex-column" v-for="i in 3" :key="i">
          <span v-for="i in 2" :key="i">- -</span>
        </div>
      </div>
    </div>
    <div v-else class="d-flex mt-5 justify-center">
      <div v-for="i in 3" :key="i">
        <v-skeleton-loader class="ma-1" type="text,text" width="100px" />
      </div>
    </div>
  </v-card>
</template>

<script>
import Doughnut from "./Chart/Doughnut.vue";
import { getComponent } from "@/models/Component.js";
import { default as ConstantMeasure } from "@/models/ConstantMeasure.js";
import { parseBRPattern } from "@/models/lib.js";
import Error from "@/components/Error/error.vue";

export default {
  name: "DoughnutCard",
  components: {
    Doughnut,
    Error,
  },
  props: {
    plants: Array,
  },
  data: () => ({
    fakeData: [
      {
        backgroundColor: ["#A5B0C066"],
        borderWidth: 0,
        data: [100],
        weight: 2,
      },
    ],
    component: null,
    options: {
      feature_name: "Portfolio",
      name: "GenerationDoughnut",
      power: "power",
      nominal: "nominalPower",
      expected: "expectedPower",
      hasExpected: true,
    },
    dataset: {
      borderWidth: 0,
      data: [1],
      backgroundColor: "#ffffff00",
      weight: 0.2,
    },
    datasets: [],
    interval: {
      instance: null,
      step: 200,
      time: 200,
      max: 5000,
    },
    percentage: 0,
    percentageExpected: 0,
    nominal: null,
    power: null,
    expected: null,
    loading: true,
    errors: [],
  }),
  watch: {
    plants: {
      handler() {
        if (this.plants.length) {
          clearInterval(this.interval.instance);
          this.interval.time = 200;
          this.loading = true;
          this.start();
        }
      },
      immediate: true,
    },
  },
  computed: {
    divisionPowerByExpected() {
      if (!this.options.hasExpected || !this.expected || !this.power) {
        return "";
      }
      const expected = this.expected.float();
      if (expected <= 0.001) {
        return "-";
      }
      let result = (this.power.float() / expected - 1) * 100;
      if (isNaN(result)) {
        return "-";
      }
      return `${result > 0 ? "+" : ""}${parseBRPattern(result.toFixed(2))}%`;
    },
    measuresAreReady() {
      return Boolean(this.power && this.expected && this.nominal);
    },
  },
  methods: {
    async start() {
      try {
        this.errors = [];
        await this.fetchComponent();
        await this.fetchMeasures();

        this.validateMeasures();
        const { nominal } = this.options;
        this.nominal = this.calculateConstant(nominal);
        this.setUpdate();
      } catch (error) {
        console.error(error);
        this.errors.push(error);
      }
      setTimeout(() => {
        this.loading = false;
      }, 300);
    },
    async fetchComponent() {
      const { feature_name, name } = this.options;
      if (!this.component) {
        this.component = await getComponent({ feature_name, name });
        this.component && this.component.connectMQTT();
      }
      if (!this.component) {
        throw `Component were not found \nFeatureName: ${feature_name}\nName: ${name}`;
      }
    },
    async fetchMeasures() {
      const plants = this.plants;
      await this.component.setRealtime({ plants, source: "plant" });
      this.component.unsubscribe();
      this.component.subscribe();
      await this.component.setConstant({ plants, source: "plant" });
    },
    /**
     * Validates if measures need are correctly configurated
     * also check if Expected Power will be rendered by checking it existence
     * in array of realtime measurements
     */
    validateMeasures() {
      const { power, expected, nominal } = this.options;
      const { constantMeasures, realtimeMeasures } = this.component;
      if (constantMeasures.length && realtimeMeasures.length) {
        this.options.hasExpected = !!realtimeMeasures.find((item) => {
          return item.name === expected;
        });

        let a = realtimeMeasures.find((item) => item.name === power);
        let b = constantMeasures.find((item) => item.name === nominal);
        if (!a || !b) {
          throw Error(
            `Realtime (${power}) or Constant (${nominal}) measurements were not define correctly`
          );
        }
      }
    },
    sum(list = [], callback = () => 0) {
      let total = 0;
      list.forEach((item) => {
        total += callback(item);
      });
      const instance = new ConstantMeasure(list[0]);
      instance.multiplier = 1;
      instance.value = total ? total : null;
      return instance;
    },
    calculateConstant(name) {
      const { constantMeasures } = this.component;
      const constantList = constantMeasures.filter(
        (measure) => measure.name === name
      );
      return this.sum(constantList, (item) => item.float());
    },
    calculateRealtime(name) {
      const { realtimeMeasures } = this.component;
      const realtimeList = realtimeMeasures.filter(
        (measure) => measure.name === name
      );
      return this.sum(realtimeList, (item) =>
        item.check() ? item.float() : 0
      );
    },
    /**
     * Function Runs every setted time recursively
     */
    setUpdate() {
      let { time, max, step } = this.interval;
      clearInterval(this.interval.instance);
      this.interval.instance = setInterval(() => {
        this.power = this.calculateRealtime(this.options.power);
        this.expected = this.calculateRealtime(this.options.expected);
        this.buildDataset();

        if (time < max) {
          this.interval.time += step;
        }
        this.setUpdate();
      }, time);
    },
    /**
     * Calculates percentage based on value an it max,
     * if max is 0 returns a default value that render
     * an empty doughnut
     * @return {Array} contains bar percentage
     */
    getPercentage(value, max) {
      if (max > 0) {
        const percentage = (value / max) * 100;
        const inverse = percentage < 100 ? 100 - percentage : 0;
        return [percentage, inverse];
      }
      return [0, 100];
    },
    buildDataset() {
      let power = this.getPercentage(this.power.float(), this.nominal.float());
      this.percentage = !isNaN(power[0])
        ? parseBRPattern(power[0].toFixed(2))
        : "-";
      let expectedPower = this.getPercentage(
        this.expected.float(),
        this.nominal.float()
      );
      this.percentageExpected = expectedPower[0];

      let data = [];

      if (this.options.hasExpected) {
        let expectedDataset = {
          borderWidth: 0,
          weight: 1,
          backgroundColor: ["#E16F1C", "#A5B0C066"],
          data: expectedPower,
        };
        data.push(expectedDataset);
        data.push(this.dataset);
      }

      let powerDataset = {
        borderWidth: 0,
        weight: 2,
        backgroundColor: ["#72C4D6", "#A5B0C066"],
        data: power,
      };
      data.push(powerDataset);
      this.datasets = [...data];
    },
  },
  beforeDestroy() {
    if (this.component) {
      this.component.unsubscribeMeasures();
    }
    clearInterval(this.interval.instance);
  },
};
</script>

<style scoped>
.card-grid {
  display: grid;
  grid-template: 40px auto 40px / 100%;
  padding: 15px 15px;
  height: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.card-title {
  text-align: left;
  padding-left: 5px;
}
.chart {
  height: 160px;
}
.bottom-text {
  display: flex;
  justify-content: space-around;
  padding: 0 2px;
}

.bottom-text > div {
  display: flex;
  flex-direction: column;
}

.chart-text {
  font-size: 18px;
  font-weight: 500;
}

@media (max-width: 280px) and (max-width: 320px) {
  .v-skeleton-loader {
    width: 70px !important;
  }
}

@media (max-width: 1280px) and (min-width: 800px) {
  .v-skeleton-loader {
    width: 70px !important;
  }
}

@media (min-width: 1281px) {
  .v-skeleton-loader {
    width: 115px !important;
  }
}
.novalues {
  display: flex;
  flex-direction: row !important;
  justify-content: space-around;
  width: 100%;
}
.doughnut-error {
  position: absolute;
  right: 5px;
  top: 5px;
}
</style>
